import React, { useState, useRef, useEffect } from 'react';

interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
}

const TimePicker: React.FC<TimePickerProps> = ({ value, onChange, error, placeholder = 'HH:mm', inputRef }) => {
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDisplayTime = () => {
    if (!value) return placeholder;
    const [hh, mm] = value.split(':');
    return `${String(Number(hh)).padStart(2, '0')}:${(mm || '00').slice(0, 2)}`;
  };

  const handleHourClick = (h: number) => {
    const mm = value?.split(':')[1] || '00';
    onChange(`${String(h).padStart(2, '0')}:${mm}`);
  };

  const handleMinuteClick = (m: number) => {
    const hh = value?.split(':')[0] || '00';
    onChange(`${hh}:${String(m).padStart(2, '0')}`);
  };

  const currentHH = value?.split(':')[0];
  const currentMM = value?.split(':')[1];

  return (
    <div>
      <div className="relative" ref={pickerRef}>
        {/* Input */}
        <div
          onClick={() => setShowPicker(!showPicker)}
          className="w-full border rounded-md px-3 py-2 bg-gray-100 cursor-pointer flex items-center justify-between"
        >
          <input
            ref={inputRef}
            readOnly
            aria-label="Time in 24-hour format"
            value={value ? getDisplayTime() : ''}
            placeholder={placeholder}
            className={`min-w-0 flex-1 cursor-pointer bg-transparent outline-none ${value ? 'text-gray-800' : 'placeholder:text-gray-400'}`}
          />
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>

        {/* Picker */}
        {showPicker && (
          <div className="absolute z-50 bottom-full mb-1 bg-white border rounded-md shadow-lg p-3 flex gap-2 items-center">
            {/* Hours */}
            <div className="h-40 w-14 overflow-y-scroll snap-y snap-mandatory border rounded-md">
              {Array.from({ length: 24 }, (_, h) => {
                const hh24 = String(h).padStart(2, '0');
                return (
                  <div
                    key={h}
                    onClick={() => handleHourClick(h)}
                    className={`h-10 flex items-center justify-center snap-start cursor-pointer rounded-md text-sm
                      ${currentHH === hh24 ? 'bg-blue-500 text-white font-bold' : 'hover:bg-gray-100 text-gray-700'}`}
                  >
                    {String(h).padStart(2, '0')}
                  </div>
                );
              })}
            </div>

            <span className="font-bold text-gray-600">:</span>

            {/* Minutes */}
            <div className="h-40 w-14 overflow-y-scroll snap-y snap-mandatory border rounded-md">
              {Array.from({ length: 60 }, (_, i) => (
                <div
                  key={i}
                  onClick={() => handleMinuteClick(i)}
                  className={`h-10 flex items-center justify-center snap-start cursor-pointer rounded-md text-sm
                    ${currentMM === String(i).padStart(2, '0') ? 'bg-blue-500 text-white font-bold' : 'hover:bg-gray-100 text-gray-700'}`}
                >
                  {String(i).padStart(2, '0')}
                </div>
              ))}
            </div>

            {/* Done */}
            <button
              type="button"
              onClick={() => setShowPicker(false)}
              className="self-end px-3 py-1 bg-blue-500 text-white rounded-md text-sm"
            >
              Done
            </button>
          </div>
        )}
      </div>

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

export default TimePicker;
